import React, { useCallback, useState } from "react";
import {
  Alert,
  Button,
  Code,
  Loader,
  Progress,
  Stack,
  Table,
  Text,
  Title,
  Group,
  Paper,
  ScrollArea,
} from "@mantine/core";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { IconDownload, IconUpload } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { API_URL } from "../../../config";
import authService from "../../../services/authService";

/** One row from the input CSV: menuid, lang, question_type, question */
export interface TestingInputRow {
  menuid: string;
  lang: string;
  question_type: string;
  question: string;
}

/** One row in the result: input fields plus answer, classification, menuItems, and optional error */
export interface TestingResultRow extends TestingInputRow {
  answer: string;
  /** Classification of the question (e.g. GET_RECOMMENDED_ITEMS) */
  classification?: string;
  /** Comma-separated item names (link text) from [text](menu_item:id) in the answer */
  menuItems?: string;
  error?: string;
}

const EXPECTED_HEADERS = ["menuid", "lang", "question_type", "question"];
const MAX_ROWS = 100;

/**
 * Parse a single CSV line respecting quoted fields (RFC 4180-style).
 * Fields containing commas or newlines should be wrapped in double quotes.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      let field = "";
      i += 1;
      while (i < line.length) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            field += '"';
            i += 2;
          } else {
            i += 1;
            break;
          }
        } else {
          field += line[i];
          i += 1;
        }
      }
      out.push(field);
    } else {
      let field = "";
      while (i < line.length && line[i] !== ",") {
        field += line[i];
        i += 1;
      }
      out.push(field.trim());
      if (i < line.length) i += 1;
    }
  }
  return out;
}

/**
 * Parse CSV text with header row into array of objects.
 * Expected columns: menuid, lang, question_type, question (case-insensitive).
 */
function parseTestingCsv(csvText: string): TestingInputRow[] {
  const lines = csvText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const headerLine = lines[0];
  const headers = parseCsvLine(headerLine).map((h) => h.toLowerCase().trim());
  const menuidIdx = headers.indexOf("menuid");
  const langIdx = headers.indexOf("lang");
  const questionTypeIdx = headers.indexOf("question_type");
  const questionIdx = headers.indexOf("question");

  if (menuidIdx === -1 || questionIdx === -1) {
    throw new Error("CSV must have headers: menuid, lang, question_type, question");
  }

  const rows: TestingInputRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]);
    const menuid = (values[menuidIdx] ?? "").trim();
    const lang = (langIdx >= 0 ? values[langIdx] ?? "" : "").trim() || "en";
    const question_type = (questionTypeIdx >= 0 ? values[questionTypeIdx] ?? "" : "").trim();
    const question = (values[questionIdx] ?? "").trim();
    if (menuid && question) {
      rows.push({ menuid, lang, question_type, question });
    }
  }
  return rows;
}

/**
 * Escape a field for CSV: wrap in quotes if it contains comma, newline, or quote.
 */
function csvEscape(value: string): string {
  const s = String(value ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

/**
 * Build CSV string from result rows (menuid, lang, question_type, question, answer, classification, menuItems).
 */
function buildResultCsv(rows: TestingResultRow[]): string {
  const header = "menuid,lang,question_type,question,answer,classification,menuItems";
  const lines = rows.map(
    (r) =>
      [r.menuid, r.lang, r.question_type, r.question, r.answer, r.classification ?? "", r.menuItems ?? ""].map(csvEscape).join(",")
  );
  return [header, ...lines].join("\n");
}

/**
 * Admin page: upload a CSV of test questions, generate answers via menu prompts, download results CSV.
 * CSV format: menuid, lang, question_type, question.
 * Output CSV: menuid, lang, question_type, question, answer.
 */
export function MenuTesting() {
  const [csvFile, setCsvFile] = useState<FileWithPath | null>(null);
  const [inputRows, setInputRows] = useState<TestingInputRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ completed: number; total: number } | null>(null);
  const [resultRows, setResultRows] = useState<TestingResultRow[] | null>(null);

  const handleFileDrop = useCallback((files: FileWithPath[]) => {
    setResultRows(null);
    setParseError(null);
    setInputRows([]);
    if (files.length === 0) {
      setCsvFile(null);
      return;
    }
    const file = files[0];
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setParseError("Please upload a CSV file.");
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = (reader.result as string) || "";
        const rows = parseTestingCsv(text);
        if (rows.length === 0) {
          setParseError("No valid rows found. Ensure header is: menuid, lang, question_type, question");
        } else {
          setParseError(null);
          setInputRows(rows);
        }
      } catch (e) {
        setParseError(e instanceof Error ? e.message : "Failed to parse CSV");
        setInputRows([]);
      }
    };
    reader.readAsText(file, "UTF-8");
  }, []);

  const handleGenerate = useCallback(async () => {
    if (inputRows.length === 0) return;
    if (inputRows.length > MAX_ROWS) {
      notifications.show({
        title: "Too many rows",
        message: `Maximum ${MAX_ROWS} rows per run. Use a smaller file or split it.`,
        color: "red",
      });
      return;
    }
    setLoading(true);
    setResultRows([]);
    setProgress({ completed: 0, total: inputRows.length });
    const token = authService.getAuthToken();
    try {
      const response = await fetch(`${API_URL}/query/batch-test-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          rows: inputRows.map((r) => ({
            menuid: r.menuid,
            lang: r.lang || "en",
            question_type: r.question_type,
            question: r.question,
          })),
        }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const message = (errBody as { error?: string; details?: string }).error
          ?? (errBody as { details?: string }).details
          ?? `Request failed (${response.status})`;
        throw new Error(message);
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");
      const decoder = new TextDecoder();
      let buffer = "";
      let completedCount = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() ?? "";
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          const data = JSON.parse(chunk.slice(6)) as TestingResultRow & { done?: boolean };
          if (data.done) {
            setLoading(false);
            setProgress(null);
            notifications.show({
              title: "Done",
              message: `Generated answers for ${inputRows.length} questions.`,
              color: "green",
            });
            return;
          }
          completedCount += 1;
          setResultRows((prev) => [...(prev ?? []), data]);
          setProgress((p) => (p ? { ...p, completed: p.completed + 1 } : null));
        }
      }
      setLoading(false);
      setProgress(null);
      notifications.show({
        title: "Done",
        message: `Generated answers for ${completedCount} questions.`,
        color: "green",
      });
    } catch (err: unknown) {
      setLoading(false);
      setProgress(null);
      const message = err instanceof Error ? err.message : "Request failed";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    }
  }, [inputRows]);

  const handleDownload = useCallback(() => {
    if (!resultRows || resultRows.length === 0) return;
    const csv = buildResultCsv(resultRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `menu-test-results-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [resultRows]);

  return (
    <Stack gap="lg">
      <Title order={3}>Menu Q&A Testing</Title>
      <Text size="sm" c="dimmed">
        Upload a CSV with columns <Code>menuid</Code>, <Code>lang</Code>,{" "}
        <Code>question_type</Code>, <Code>question</Code>. Answers are generated using the
        prompt and menu for each <Code>menuid</Code>. Download a CSV with <Code>answer</Code> and{" "}
        <Code>classification</Code> and <Code>menuItems</Code> (item names from [text](menu_item:id) links). Maximum {MAX_ROWS} rows per run.
      </Text>

      <Alert title="CSV format" color="blue">
        <Text size="sm">
          Header row (case-insensitive): <Code>menuid,lang,question_type,question</Code>
          <br />
          • <strong>menuid</strong>: Menu ID (required)
          <br />
          • <strong>lang</strong>: Language code for the prompt (e.g. en, jp). Defaults to en.
          <br />
          • <strong>question_type</strong>: Label only (e.g. dietary, recommendation).
          <br />
          • <strong>question</strong>: The question to answer (required).
        </Text>
      </Alert>

      <Dropzone
        onDrop={handleFileDrop}
        accept={{ "text/csv": [".csv"], "application/csv": [".csv"] }}
        maxFiles={1}
      >
        <Group justify="center" gap="xs" style={{ pointerEvents: "none" }}>
          <IconUpload size={40} />
          <Text size="lg">Drop CSV here or click to select</Text>
        </Group>
      </Dropzone>

      {parseError && (
        <Alert color="red" title="Parse error">
          {parseError}
        </Alert>
      )}

      {csvFile && inputRows.length > 0 && (
        <Paper p="md" withBorder>
          <Text fw={500} mb="xs">
            Loaded: {csvFile.name} — {inputRows.length} row(s)
          </Text>
          <ScrollArea>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>menuid</Table.Th>
                  <Table.Th>lang</Table.Th>
                  <Table.Th>question_type</Table.Th>
                  <Table.Th>question</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {inputRows.slice(0, 10).map((row, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{row.menuid}</Table.Td>
                    <Table.Td>{row.lang || "en"}</Table.Td>
                    <Table.Td>{row.question_type}</Table.Td>
                    <Table.Td style={{ maxWidth: 280 }}>{row.question}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          {inputRows.length > 10 && (
            <Text size="sm" c="dimmed" mt="xs">
              Showing first 10 of {inputRows.length} rows.
            </Text>
          )}
          {progress && (
            <Stack gap="xs" mt="md">
              <Group gap="sm">
                <Text size="sm" fw={500}>
                  Progress: {progress.completed} / {progress.total} questions
                </Text>
              </Group>
              <Progress
                value={progress.total ? (100 * progress.completed) / progress.total : 0}
                size="md"
                radius="sm"
              />
            </Stack>
          )}
          <Button
            mt="md"
            onClick={handleGenerate}
            loading={loading}
            disabled={loading}
            leftSection={loading ? <Loader size="sm" /> : null}
          >
            Generate answers
          </Button>
        </Paper>
      )}

      {resultRows && resultRows.length > 0 && (
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={500}>Results ({resultRows.length} rows)</Text>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              onClick={handleDownload}
            >
              Download CSV
            </Button>
          </Group>
          <ScrollArea>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>menuid</Table.Th>
                  <Table.Th>lang</Table.Th>
                  <Table.Th>question_type</Table.Th>
                  <Table.Th>question</Table.Th>
                  <Table.Th>classification</Table.Th>
                  <Table.Th>answer</Table.Th>
                  <Table.Th>menuItems</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resultRows.slice(0, 15).map((row, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>{row.menuid}</Table.Td>
                    <Table.Td>{row.lang}</Table.Td>
                    <Table.Td>{row.question_type}</Table.Td>
                    <Table.Td style={{ maxWidth: 200 }}>{row.question}</Table.Td>
                    <Table.Td style={{ maxWidth: 140 }}>
                      <Text size="sm">{row.classification ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 280 }}>
                      {row.error ? (
                        <Text size="sm" c="red">
                          {row.error}
                        </Text>
                      ) : (
                        row.answer
                      )}
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 180 }}>
                      <Text size="sm" title={row.menuItems ?? ""}>
                        {row.menuItems ?? "—"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          {resultRows.length > 15 && (
            <Text size="sm" c="dimmed" mt="xs">
              Showing first 15 of {resultRows.length} rows. Use &quot;Download CSV&quot; for full data.
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}
